import type { Category, CategoryAttribute } from '../types/category';

const SYSTEM_ATTRIBUTE_KEYS = new Set([
  'public_transport',
  'possible_loss_locations',
  'found_object_id',
]);

export type ParsedAnnouncementObjectAttributes = {
  categoryAttributes: Record<string, string>;
  secretClues: string;
  publicTransportJson: string;
  possibleLossLocationsJson: string;
  extraAttributesJson: string;
};

export function parseAnnouncementObjectAttributes(
  objectAttributes: Record<string, unknown> | null | undefined
): ParsedAnnouncementObjectAttributes {
  const attrs = objectAttributes ?? {};
  const categoryAttributes: Record<string, string> = {};
  const extra: Record<string, unknown> = {};
  let secretClues = '';

  for (const [key, val] of Object.entries(attrs)) {
    if (key === 'secret_clues') {
      secretClues = val == null ? '' : String(val);
      continue;
    }
    if (SYSTEM_ATTRIBUTE_KEYS.has(key)) {
      continue;
    }
    if (val == null) {
      categoryAttributes[key] = '';
      continue;
    }
    if (typeof val === 'object') {
      categoryAttributes[key] = JSON.stringify(val);
    } else {
      categoryAttributes[key] = String(val);
    }
  }

  for (const [key, val] of Object.entries(attrs)) {
    if (
      key === 'secret_clues' ||
      SYSTEM_ATTRIBUTE_KEYS.has(key) ||
      Object.prototype.hasOwnProperty.call(categoryAttributes, key)
    ) {
      continue;
    }
    if (val !== undefined && val !== null && val !== '') {
      extra[key] = val;
    }
  }

  return {
    categoryAttributes,
    secretClues,
    publicTransportJson: formatJsonField(attrs.public_transport),
    possibleLossLocationsJson: formatJsonField(attrs.possible_loss_locations),
    extraAttributesJson:
      Object.keys(extra).length > 0 ? JSON.stringify(extra, null, 2) : '',
  };
}

function formatJsonField(value: unknown): string {
  if (value === undefined || value === null) return '';
  return JSON.stringify(value, null, 2);
}

export function getEditableCategoryAttributeEntries(
  category: Category | null | undefined
): Array<[string, CategoryAttribute]> {
  const attributes = category?.attributes;
  if (!attributes) return [];

  return Object.entries(attributes).filter(([, attr]) => {
    if (attr.disabled) return false;
    return true;
  });
}

export function mergeAnnouncementObjectAttributesForSubmit(input: {
  categoryAttributes: Record<string, string>;
  secretClues: string;
  publicTransportJson: string;
  possibleLossLocationsJson: string;
  extraAttributesJson: string;
}): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, rawValue] of Object.entries(input.categoryAttributes)) {
    const value = rawValue.trim();
    if (!value) continue;
    if ((value.startsWith('{') || value.startsWith('[')) && key !== 'secret_clues') {
      try {
        result[key] = JSON.parse(value);
        continue;
      } catch {
        // conserve la chaîne
      }
    }
    result[key] = value;
  }

  const secret = input.secretClues.trim();
  if (secret) {
    result.secret_clues = secret;
  }

  if (input.publicTransportJson.trim()) {
    result.public_transport = parseJsonField(input.publicTransportJson, 'transport en commun');
  }

  if (input.possibleLossLocationsJson.trim()) {
    result.possible_loss_locations = parseJsonField(
      input.possibleLossLocationsJson,
      'lieux de perte possibles'
    );
  }

  if (input.extraAttributesJson.trim()) {
    const extra = parseJsonField<Record<string, unknown>>(
      input.extraAttributesJson,
      'attributs supplémentaires'
    );
    for (const [key, value] of Object.entries(extra)) {
      if (value !== undefined && value !== null && value !== '') {
        result[key] = value;
      }
    }
  }

  return result;
}

function parseJsonField<T>(value: string, fieldLabel: string): T {
  try {
    return JSON.parse(value) as T;
  } catch {
    throw new Error(`Le champ ${fieldLabel} doit contenir du JSON valide.`);
  }
}

export function applyCategoryAttributeCascade(
  key: string,
  value: string,
  previous: Record<string, string>
): Record<string, string> {
  const next = { ...previous, [key]: value };

  if (key === 'brand' || key === 'marque') {
    delete next.model;
    delete next.modele;
  }
  if (key === 'clothing_type' || key === 'category_type') {
    delete next.size;
    delete next.subtype;
  }

  return next;
}
